<div class="manage-plans-container">
  <!-- Header with Page Title and Page Size Selector -->
  <div class="manage-plans-headers">
    <div class="header-info">
      <div class="page-title">Subscription Plans</div>
      <div class="page-description">
        Manage Organization subscription plans, pricing schedules, and assigned
        organizations.
      </div>
    </div>
    @if (!isLoading && summary.total > 0) {
      <div class="page-size">
        <app-page-size
          [pageSize]="pageSize"
          [selectId]="'subscriptionPlanIds'"
          (pageSizeChanged)="onPageSizeChange($event)"
        ></app-page-size>
      </div>
    }
  </div>

  <!-- Dashboard Stat Cards matching manage-duplicates style -->
  <div class="dashboard-container">
    @if (isLoading) {
      <div class="stats-grid">
        <div class="skeleton-box"></div>
        <div class="skeleton-box"></div>
        <div class="skeleton-box"></div>
      </div>
    } @else {
      <div class="stats-grid">
        <!-- Total Plans Card -->
        <div
          class="stat-card email-card"
          [class.active]="subPlanFilter === 'all'"
          (click)="applyFilter('all')"
          (keydown.enter)="applyFilter('all')"
          role="button"
          tabindex="0"
        >
          <svg
            class="stat-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
          </svg>
          <div class="stat-content">
            <h3>All Plans</h3>
            <div class="stat-value">
              {{ summary.total }}
            </div>
          </div>
        </div>

        <!-- Active Plans Card -->
        <div
          class="stat-card complete-card"
          [class.active]="subPlanFilter === 'active'"
          (click)="applyFilter('active')"
          (keydown.enter)="applyFilter('active')"
          role="button"
          tabindex="0"
        >
          <svg
            class="stat-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <div class="stat-content">
            <h3>Active Plans</h3>
            <div class="stat-value">
              {{ summary.active }}
            </div>
          </div>
        </div>

        <!-- Archived Plans Card -->
        <div
          class="stat-card rate-card"
          [class.active]="subPlanFilter === 'archive'"
          (click)="applyFilter('archive')"
          (keydown.enter)="applyFilter('archive')"
          role="button"
          tabindex="0"
        >
          <svg
            class="stat-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 8v13H3V8" />
            <path d="M1 3h22v5H1z" />
            <path d="M10 12h4" />
          </svg>
          <div class="stat-content">
            <h3>Archived Plans</h3>
            <div class="stat-value">
              {{ summary.archived }}
            </div>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Filter, Search & Create Action Bar -->
  <div class="filter-search-section">
    <div class="left-section">
      <div class="search-section-wrapper">
        <app-search-input
          [placeholder]="'Search by plan name or code...'"
          [(searchQuery)]="search"
          (searchQueryChange)="onSearchQueryChange($event)"
        ></app-search-input>
      </div>
    </div>

    <div class="right-section">
      <button
        type="button"
        class="action-btn create-btn"
        (click)="onCreatePlan()"
      >
        <app-svg-icon
          [iconName]="'add.svg'"
          [height]="'14px'"
          [width]="'14px'"
          [style.fill]="'#fff'"
        ></app-svg-icon>
        <span>Create Plan</span>
      </button>
    </div>
  </div>

  <!-- Shared App Table for Subscription Plans -->
  <div class="plans-table-card">
    <app-table
      [columns]="tableColumns"
      [data]="subscriptionPlans"
      [loading]="isLoading"
      [noResultsMessage]="
        'No subscription plans match your search or filter criteria.'
      "
      [skeletonRows]="5"
    >
      <ng-template let-plan>
        <tr class="plan-table-row">
          <!-- Plan Name & Code -->
          <td>
            <div class="plan-info">
              <div class="plan-title" [title]="plan.display_name">
                {{ plan.display_name }}
              </div>
              <div class="plan-slug" [title]="plan.plan_name">
                <code>{{ plan.plan_name }}</code>
              </div>
            </div>
          </td>

          <!-- Status -->
          <td>
            <span
              class="status-pill"
              [class.active]="plan.is_active"
              [class.archived]="!plan.is_active"
            >
              {{ plan.is_active ? "Active" : "Archived" }}
            </span>
          </td>

          <!-- Trial Period -->
          <td>
            @if (plan.trial_period_days > 0) {
              <div class="table-metric-cell">
                <span class="metric-count">{{ plan.trial_period_days }}</span>
                <span class="metric-label">{{
                  plan.trial_period_days === 1 ? "day" : "days"
                }}</span>
              </div>
            } @else {
              <span class="empty-cell-text">No trial</span>
            }
          </td>

          <!-- Features Count -->
          <td>
            @if (plan.features_count > 0) {
              <div class="table-metric-cell">
                <span class="metric-count">{{ plan.features_count }}</span>
                <span class="metric-label">{{
                  plan.features_count === 1 ? "feature" : "features"
                }}</span>
              </div>
            } @else {
              <span class="empty-cell-text">No features</span>
            }
          </td>

          <!-- Connections -->
          <td>
            <div class="connection-stats">
              <div class="stat-row">
                <span class="count">{{
                  plan.subscribed_connections_count
                }}</span>
                <span class="label">Subscribed</span>
              </div>
              <div class="stat-row eligible">
                <span class="count">{{ plan.connections_count }}</span>
                <span class="label">Eligible</span>
              </div>
            </div>
          </td>

          <!-- Last Updated -->
          <td>
            <span class="date-text">
              {{ plan.updated_at | date: "MMM d, y, h:mm a" }}
            </span>
          </td>

          <!-- Action -->
          <td>
            <div class="action-icons">
              <a href="javascript:void(0)" (click)="onPlan(plan)"> View </a>
            </div>
          </td>
        </tr>
      </ng-template>
    </app-table>
  </div>

  <!-- Pagination Component -->
  @if (!isLoading && plansCount > pageSize) {
    <div class="pagination-wrapper">
      <app-pagination
        [currentPage]="currentPage"
        [pageSize]="pageSize"
        [totalItems]="plansCount"
        (pageChanged)="onPageChange($event)"
      ></app-pagination>
    </div>
  }
</div>
